import { Decoder, nullable } from 'json-bouncer'
import * as R from 'ramda'
import { FeedbackLoop } from '../../Data/FeedbackLoop'
import * as Downloadable from '../../Data/Downloadable'
import * as DownloadableFile from '../../Data/DownloadableFile'
import {
    mandatoryStringValidator,
    minimumValidator,
    stringToFixedPointNumberValidator,
    validateInputField,
} from '../../Data/FormValidation'
import { decodeHttpResponse, HttpError } from '../../Data/Http'
import { HttpRequest } from '../../Data/HttpRequest'
import * as Nullable from '../../Data/Nullable'
import { update } from '../../Data/ObjectHelper'
import * as Result from '../../Data/Result'
import { Session } from '../../Data/Session'
import * as PrismaFile from '../../Entities/PrismaFile'
import * as PrismaFileType from '../../Entities/PrismaFileType'
import { prismaRequestCommentWithUsersDecoder } from '../../Entities/PrismaRequestCommentWithUsers'
import * as PrismaRequestOfferType from '../../Entities/PrismaRequestOfferType'
import * as PrismaRequestOfferWithManufacturerStatus
    from '../../Entities/PrismaRequestOfferWithManufacturerStatus'
import * as PrismaRequestWithDetails
    from '../../Entities/PrismaRequestWithDetails'
import * as PrismaFileResource from '../../Request/PrismaFileResource'
import * as PrismaRequestCommentResource
    from '../../Request/PrismaRequestCommentResource'
import * as PrismaRequestOfferResource
    from '../../Request/PrismaRequestOfferResource'
import * as PrismaRequestResource from '../../Request/PrismaRequestResource'
import { PrismaRequestDetailsAction } from './PrismaRequestDetailsActions'
import {
    PrismaRequestDetailsDownloadRequestIdentifier,
    PrismaRequestDetailsFeedback,
    PrismaRequestDetailsRequestIdentifier,
} from './PrismaRequestDetailsFeedback'
import { OfferForm, PrismaRequestDetailsState } from './PrismaRequestDetailsState'
import { PrismaRequestMarkAsDispatchedResponse } from '../../Entities/PrismaRequestMarkAsDispatchedResponse'

type PrismaRequestDetailsLoop =
    FeedbackLoop<PrismaRequestDetailsState, PrismaRequestDetailsFeedback>

export function activate(session: Session, id: number): HttpRequest {
    return PrismaRequestResource.retrieveWithDetails(
        session.manufacturer.id,
        id,
    )
}

export function initialDecoder(): Decoder<PrismaRequestWithDetails.M> {
    return PrismaRequestWithDetails.decoder()
}

export function init(
    {
        prismaRequest,
        printOffer,
        lithoOffer,
        bpsUser,
        translatedProductAttributes,
        prismaRequestCommentsWithUsers,
        printProduct,
        attachmentFiles,
        proofCopyFiles,
        printFiles,
        printSapOrderNumber,
        lithoSapOrderNumber,
        assignedAdminName,
        assignedAdminEmail,
        deliveryInformation,
        selectableParcelDispatchers,
    }: PrismaRequestWithDetails.M,
): PrismaRequestDetailsState {

    const selectedParcelDispatcher = selectableParcelDispatchers ? selectableParcelDispatchers[0] : null

    return {
        prismaRequest,
        printOffer: Nullable.map(initOfferForm, printOffer),
        lithoOffer: Nullable.map(initOfferForm, lithoOffer),
        bpsUser,
        translatedProductAttributes,
        prismaRequestCommentsWithUsers,
        printProduct,
        attachmentFiles: fromFiles(attachmentFiles),
        proofCopyFiles: fromFiles(proofCopyFiles),
        printFiles: fromFiles(printFiles),
        commentInput: {
            text: '',
        },
        commentValidation: {
            text: null,
        },
        commentSubmissionAttempted: false,
        commentLoading: false,
        commentError: null,
        printSapOrderNumber,
        lithoSapOrderNumber,
        assignedAdminName,
        assignedAdminEmail,
        deliveryInformation,
        selectableParcelDispatchers,
        selectedParcelDispatcher: selectedParcelDispatcher,
        trackingNumber: null,
        isSendingMarkAsDispatchedRequest: false,
        isMarkAsDispatchedModalOpen: false
    }
}

const fromFiles: <T>(files: T[]) => DownloadableFile.M<T>[] =
    R.map(DownloadableFile.fromFile)

function initOfferForm(
    {
        prismaRequestOffer,
        manufacturerStatus,
    }: PrismaRequestOfferWithManufacturerStatus.M,
): OfferForm {
    return {
        prismaRequestOffer,
        manufacturerStatus,
        priceInput: {
            price: '',
        },
        priceValidation: {
            price: null,
        },
        isPriceSubmitModalOpen: false,
        isDeclineOfferModalOpen: false,
        declineComment: '',
        priceSubmissionAttempted: false,
        loading: false,
        error: null,
    }
}

export function reduce(
    session: Session,
    state: PrismaRequestDetailsState,
    action: PrismaRequestDetailsAction,
): PrismaRequestDetailsLoop {
    switch (action.type) {
        case 'MenuButtonClicked':
            return {state, feedback: [{type: 'OpenDrawer'}]}
        case 'PriceChanged':
            return reduceOfferForm(
                offerForm => {
                    return {
                        state: update(
                            {
                                priceInput: update(
                                    {price: action.value},
                                    offerForm.priceInput,
                                ),
                                priceValidation: update(
                                    {
                                        price:
                                            validateInputField(
                                                priceValidators.price,
                                                offerForm
                                                    .priceSubmissionAttempted,
                                                action.value,
                                            ),
                                    },
                                    offerForm.priceValidation,
                                ),
                            },
                            offerForm,
                        ),
                        feedback: [],
                    }
                },
                action.offerType,
                state,
            )
        case 'PriceSubmit':
            return reduceOfferForm(
                offerForm => {
                    const validationResult =
                        priceValidators.price(offerForm.priceInput.price)
                    return (() => {
                        switch (validationResult.type) {
                            case 'Ok':
                                const updateRequest: PrismaRequestDetailsFeedback = {
                                    type: 'SendRequest',
                                    identifier: {
                                        type: 'PrismaRequestOfferPriceUpdate',
                                        offerType: action.offerType,
                                    },
                                    httpRequest:
                                        PrismaRequestOfferResource.priceUpdate(
                                            offerForm.prismaRequestOffer.id,
                                            {netPrice: validationResult.value},
                                        ),
                                }
                                return {
                                    state: update({loading: true}, offerForm),
                                    feedback: [updateRequest],
                                }
                            case 'Err':
                                return {
                                    state: update(
                                        {
                                            priceSubmissionAttempted: true,
                                            priceValidation: {
                                                price: validationResult.error,
                                            },
                                        },
                                        offerForm,
                                    ),
                                    feedback: [],
                                }
                        }
                    })()
                },
                action.offerType,
                state,
            )
        case 'DeclineOffer':
            return reduceOfferForm(
                offerForm => {

                    const declineOfferRequest: PrismaRequestDetailsFeedback = {
                        type: 'SendRequest',
                        identifier: {
                            type: 'PrismaRequestOfferDecline',
                            offerType: action.offerType,
                        },
                        httpRequest:
                            PrismaRequestOfferResource.declineOffer(
                                offerForm.prismaRequestOffer.id,
                                {comment: action.comment},
                            ),
                    }

                    return {
                        state: update({loading: true}, offerForm),
                        feedback: [declineOfferRequest],
                    }
                },
                action.offerType,
                state,
            )
        case 'PrismaRequestOfferDeclineResponse': {
            const result =
                decodeHttpResponse(
                    nullable(prismaRequestCommentWithUsersDecoder()),
                    action.result,
                )

            return (() => {
                switch (result.type) {
                    case 'Ok':
                        let newStateWithComment = state
                        if (result.value !== null) {
                            newStateWithComment = update(
                                {
                                    commentLoading: false,
                                    prismaRequestCommentsWithUsers:
                                        R.append(
                                            result.value,
                                            state.prismaRequestCommentsWithUsers,
                                        ),
                                    commentError: null,
                                    commentInput: {text: ''},
                                    commentValidation: {text: null},
                                    commentSubmissionAttempted: false,
                                },
                                state,
                            )
                        }
                        return reduceOfferForm(
                            offerForm => {
                                return {
                                    state: update(
                                        {
                                            loading: false,
                                            manufacturerStatus: 'Declined',
                                        },
                                        offerForm,
                                    ),
                                    feedback: [],
                                }
                            },
                            action.offerType,
                            newStateWithComment,
                        )
                    case 'Err':
                        return {
                            state: update(
                                {
                                    commentLoading: false,
                                    commentError: result.error,
                                },
                                state,
                            ),
                            feedback: [],
                        }
                }
            })()
        }
        case 'PrismaRequestOfferPriceUpdateResponse': {
            return reduceOfferForm(
                offerForm => {
                    const result =
                        decodeHttpResponse(
                            PrismaRequestOfferWithManufacturerStatus.decoder(),
                            action.result,
                        )
                    return (() => {
                        switch (result.type) {
                            case 'Ok':
                                return {
                                    state: update(
                                        {
                                            loading: false,
                                            error: null,
                                            prismaRequestOffer:
                                            result.value.prismaRequestOffer,
                                            manufacturerStatus:
                                            result.value.manufacturerStatus,
                                        },
                                        offerForm,
                                    ),
                                    feedback: [],
                                }
                            case 'Err':
                                return {
                                    state: update(
                                        {
                                            loading: false,
                                            error: result.error,
                                        },
                                        offerForm,
                                    ),
                                    feedback: [],
                                }
                        }
                    })()
                },
                action.offerType,
                state,
            )
        }
        case 'DownloadPrismaFile':
            return {
                state: updateAttachmentDownload(
                    action.prismaFileType,
                    action.id,
                    {type: 'Loading'},
                    state,
                ),
                feedback: [{
                    type: 'SendDownloadRequest',
                    identifier: {
                        type: 'PrismaFileDownload',
                        prismaFileType: action.prismaFileType,
                        id: action.id,
                    },
                    httpRequest: PrismaFileResource.download(action.id),
                    filename: action.filename,
                }],
            }
        case 'PrismaFileDownloadResponse':
            return {
                state:
                    updateAttachmentDownload(
                        action.prismaFileType,
                        action.id,
                        Downloadable.fromResult(action.result),
                        state,
                    ),
                feedback: [],
            }
        case 'CommentTextChanged':
            return {
                state: update(
                    {
                        commentInput: update(
                            {text: action.value},
                            state.commentInput,
                        ),
                        commentValidation: update(
                            {
                                text:
                                    validateInputField(
                                        commentValidators.text,
                                        state.commentSubmissionAttempted,
                                        action.value,
                                    ),
                            },
                            state.commentValidation,
                        ),
                    },
                    state,
                ),
                feedback: [],
            }
        case 'CommentSubmit': {
            const result = commentValidators.text(state.commentInput.text)
            return ((): PrismaRequestDetailsLoop => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update({commentLoading: true}, state),
                            feedback: [{
                                type: 'SendRequest',
                                identifier: {type: 'PrismaRequestCommentCreate'},
                                httpRequest:
                                    PrismaRequestCommentResource.create({
                                                                            prismaRequestId: state.prismaRequest.id,
                                                                            manufacturerId: session.manufacturer.id,
                                                                            text: result.value,
                                                                        }),
                            }],
                        }
                    case 'Err':
                        return {
                            state: update(
                                {
                                    commentValidation: update(
                                        {text: result.error},
                                        state.commentValidation,
                                    ),
                                    commentSubmissionAttempted: true,
                                },
                                state,
                            ),
                            feedback: [],
                        }
                }
            })()
        }
        case 'PrismaRequestCommentCreateResponse': {
            const result =
                decodeHttpResponse(
                    prismaRequestCommentWithUsersDecoder(),
                    action.result,
                )
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    prismaRequestCommentsWithUsers:
                                        R.append(
                                            result.value,
                                            state.prismaRequestCommentsWithUsers,
                                        ),
                                    commentLoading: false,
                                    commentError: null,
                                    commentInput: {text: ''},
                                    commentValidation: {text: null},
                                    commentSubmissionAttempted: false,
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    case 'Err':
                        return {
                            state: update(
                                {
                                    commentLoading: false,
                                    commentError: result.error,
                                },
                                state,
                            ),
                            feedback: [],
                        }
                }
            })()
        }
        case 'UploadPrintFile': {
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'PrintPrismaFileCreate'},
                    httpRequest: PrismaFileResource.upload(
                        state.prismaRequest.id,
                        action.file,
                        'Print',
                    ),
                }],
            }
        }
        case 'UploadProofCopyFile': {
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'ProofCopyPrismaFileCreate'},
                    httpRequest: PrismaFileResource.upload(
                        state.prismaRequest.id,
                        action.file,
                        'ProofCopy',
                    ),
                }],
            }
        }
        case 'UploadAttachmentFile': {
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'AttachmentPrismaFileCreate'},
                    httpRequest: PrismaFileResource.upload(
                        state.prismaRequest.id,
                        action.file,
                        'Attachment',
                    ),
                }],
            }
        }
        case 'PrintPrismaFileCreateResponse': {
            const result =
                decodeHttpResponse(PrismaFile.decoder(), action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    printFiles:
                                        R.append(
                                            DownloadableFile.fromFile(
                                                result.value,
                                            ),
                                            state.printFiles,
                                        ),
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    case 'Err':
                        // tslint:disable-next-line no-console
                        console.log(result.error)
                        return {state, feedback: []}
                }
            })()
        }
        case 'AttachmentPrismaFileCreateResponse': {
            const result =
                decodeHttpResponse(PrismaFile.decoder(), action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    attachmentFiles:
                                        R.append(
                                            DownloadableFile.fromFile(
                                                result.value,
                                            ),
                                            state.attachmentFiles,
                                        ),
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    case 'Err':
                        // tslint:disable-next-line no-console
                        console.log(result.error)
                        return {state, feedback: []}
                }
            })()
        }
        case 'ProofCopyPrismaFileCreateResponse': {
            const result =
                decodeHttpResponse(PrismaFile.decoder(), action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    proofCopyFiles:
                                        R.append(
                                            DownloadableFile.fromFile(
                                                result.value,
                                            ),
                                            state.proofCopyFiles,
                                        ),
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    case 'Err':
                        // tslint:disable-next-line no-console
                        console.log(result.error)
                        return {state, feedback: []}
                }
            })()
        }
        case 'OpenPriceSubmitModal': {
            return reduceOfferForm(
                offerForm => {
                    return {
                        state: update(
                            {
                                isPriceSubmitModalOpen: true,
                            },
                            offerForm,
                        ),
                        feedback: [],
                    }
                },
                action.offerType,
                state,
            )
        }
        case 'ClosePriceSubmitModal': {
            return reduceOfferForm(
                offerForm => {
                    return {
                        state: update(
                            {
                                isPriceSubmitModalOpen: false,
                            },
                            offerForm,
                        ),
                        feedback: [],
                    }
                },
                action.offerType,
                state,
            )
        }
        case 'OpenDeclineOfferModal': {
            return reduceOfferForm(
                offerForm => {
                    return {
                        state: update(
                            {
                                isDeclineOfferModalOpen: true,
                            },
                            offerForm,
                        ),
                        feedback: [],
                    }
                },
                action.offerType,
                state,
            )
        }
        case 'CloseDeclineOfferModal': {
            return reduceOfferForm(
                offerForm => {
                    return {
                        state: update(
                            {
                                isDeclineOfferModalOpen: false,
                            },
                            offerForm,
                        ),
                        feedback: [],
                    }
                },
                action.offerType,
                state,
            )
        }
        case 'ChangeDeclineComment': {
            return reduceOfferForm(
                offerForm => {
                    return {
                        state: update(
                            {
                                declineComment: action.comment,
                            },
                            offerForm,
                        ),
                        feedback: [],
                    }
                },
                action.offerType,
                state,
            )
        }
        case 'ChangeTrackingNumber': {
            return {
                state: update({trackingNumber: action.value}, state),
                feedback: [],
            }
        }
        case 'ChangeSelectedParcelDispatcher': {
            return {
                state: update({selectedParcelDispatcher: action.value}, state),
                feedback: [],
            }
        }
        case 'ToggleMarkAsDispatchedModal': {
            return {
                state: {
                    ...state,
                    isMarkAsDispatchedModalOpen: action.open
                },
                feedback: [],
            }
        }
        case 'MarkAsDispatched': {
            if (state.selectedParcelDispatcher) {
                return {
                    state: {
                        ...state,
                        isSendingMarkAsDispatchedRequest: true,
                        isMarkAsDispatchedModalOpen: false
                    },
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {type: 'MarkAsDispatched'},
                        httpRequest: PrismaRequestResource.markAsDispatched(
                            state.prismaRequest.id,
                            {
                                parcelDispatcherId: state.selectedParcelDispatcher.id,
                                trackingNumber: state.trackingNumber ? state.trackingNumber : '',
                            },
                        ),
                    }],
                }
            }
            return {
                state,
                feedback: [],
            }
        }
        case 'MarkAsDispatchedResponse': {
            const result =
                decodeHttpResponse(PrismaRequestMarkAsDispatchedResponse.decoder(), action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        if (state.deliveryInformation) {
                            return {
                                state: {
                                    ...state,
                                    deliveryInformation: {
                                        ...state.deliveryInformation,
                                        parcel: result.value.parcel,
                                        parcelDispatcher: result.value.parcelDispatcher,
                                    },
                                    isSendingMarkAsDispatchedRequest: false,
                                },
                                feedback: [],
                            }
                        }
                        return {state, feedback: []}
                    case 'Err':
                        // tslint:disable-next-line no-console
                        console.log(result.error)
                        return {
                            state: {
                                ...state,
                                isSendingMarkAsDispatchedRequest: false,
                            },
                            feedback: [],
                        }
                }
            })()
        }
    }
}

function reduceOfferForm(
    fn: (offerForm: OfferForm) =>
        FeedbackLoop<OfferForm, PrismaRequestDetailsFeedback>,
    offerType: PrismaRequestOfferType.M,
    state: PrismaRequestDetailsState,
): PrismaRequestDetailsLoop {
    const offerForm =
        (() => {
            switch (offerType) {
                case 'Print':
                    return state.printOffer
                case 'Litho':
                    return state.lithoOffer
            }
        })()
    if (offerForm === null) {
        return {state, feedback: []}
    }
    const
        offerFormLoop = fn(offerForm),
        newState =
            (() => {
                switch (offerType) {
                    case 'Print':
                        return update({printOffer: offerFormLoop.state}, state)
                    case 'Litho':
                        return update({lithoOffer: offerFormLoop.state}, state)
                }
            })()
    return {
        state: newState,
        feedback: offerFormLoop.feedback,
    }
}

function updateAttachmentDownload(
    prismaFileType: PrismaFileType.M,
    id: number,
    download: Downloadable.Downloadable,
    state: PrismaRequestDetailsState,
): PrismaRequestDetailsState {
    const filesUpdate =
        R.map<DownloadableFile.M<PrismaFile.M>, DownloadableFile.M<PrismaFile.M>>(
            downloadableFile =>
                downloadableFile.file.id === id
                    ? update({download}, downloadableFile)
                    : downloadableFile,
        )
    switch (prismaFileType) {
        case 'Attachment':
            return update(
                {attachmentFiles: filesUpdate(state.attachmentFiles)},
                state,
            )
        case 'ProofCopy':
            return update(
                {proofCopyFiles: filesUpdate(state.proofCopyFiles)},
                state,
            )
        case 'Print':
            return update(
                {printFiles: filesUpdate(state.printFiles)},
                state,
            )
    }
}

export function httpResponseAction(
    identifier: PrismaRequestDetailsRequestIdentifier,
    result: Result.Result<HttpError, string>,
): PrismaRequestDetailsAction {
    switch (identifier.type) {
        case 'PrismaRequestOfferPriceUpdate':
            return {
                type: 'PrismaRequestOfferPriceUpdateResponse',
                result,
                offerType: identifier.offerType,
            }
        case 'PrismaRequestOfferDecline':
            return {
                type: 'PrismaRequestOfferDeclineResponse',
                result,
                offerType: identifier.offerType,
            }
        case 'PrismaRequestCommentCreate':
            return {
                type: 'PrismaRequestCommentCreateResponse',
                result,
            }
        case 'PrintPrismaFileCreate':
            return {
                type: 'PrintPrismaFileCreateResponse',
                result,
            }
        case 'AttachmentPrismaFileCreate':
            return {
                type: 'AttachmentPrismaFileCreateResponse',
                result,
            }
        case 'ProofCopyPrismaFileCreate':
            return {
                type: 'ProofCopyPrismaFileCreateResponse',
                result,
            }
        case 'MarkAsDispatched':
            return {
                type: 'MarkAsDispatchedResponse',
                result,
            }
    }
}

export function httpDownloadResponseAction(
    identifier: PrismaRequestDetailsDownloadRequestIdentifier,
    result: Result.Result<HttpError, null>,
): PrismaRequestDetailsAction {
    switch (identifier.type) {
        case 'PrismaFileDownload':
            return {
                type: 'PrismaFileDownloadResponse',
                prismaFileType: identifier.prismaFileType,
                id: identifier.id,
                result,
            }
    }
}

const priceValidators = {
    price:
        Result.compose(
            minimumValidator(0),
            Result.compose(
                stringToFixedPointNumberValidator(2),
                mandatoryStringValidator(),
            ),
        ),
}

const commentValidators = {
    text: mandatoryStringValidator(),
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/PrismaRequestDetails/PrismaRequestDetailsReducer.ts